/**
 * @file     brandConfig.ts
 * @purpose  Defines static brand identity constants: agent name, brokerage name, contact details, and website base URL.
 */
export const brandConfig = {
  agentName: 'Ray Rasouli',
  brokerageName: 'Real Broker B.C. Ltd.',
  brokerageAddress: '500-666 Burrard Street, Vancouver, BC V6C 3P6',
  /**
   * The CASL mailing address (SOR/2012-36 s.2(1)(d)) printed in every
   * commercial email and text, and in every consent request. It must reach Ray
   * for at least 60 days after each send, so it is kept apart from the
   * brokerage office address, which RESR s.40(4) governs separately.
   */
  mailingAddress: '4609-1480 Howe Street, Vancouver, BC V6Z 0G5',
  companyDisplayName: 'Relta Real Estate',
  /** Short names the brand is searched and referred to by. */
  brandAlternateNames: ['Relta', 'relta.ca'] as readonly string[],
  /**
   * Ray's licence as the regulator records it. Public on the BCFSA licensee
   * register, so it is safe to print and to emit as a schema.org credential.
   */
  licence: {
    number: 'RE611959',
    category: 'Real estate trading services',
    regulatorName: 'BC Financial Services Authority',
    regulatorShortName: 'BCFSA',
    regulatorUrl: 'https://www.bcfsa.ca',
    registerUrl: 'https://www.bcfsa.ca/re-licencee/RE611959',
    jurisdiction: 'British Columbia',
  },
  boardMembership: 'Greater Vancouver REALTORS®',
  /** Headshot used on /about-ray and as the Person schema image. */
  agentHeadshotPath: '/about/ray_rasouli_real_estate_vancouver_bc.png',
  /**
   * Client quotes with written consent on file (BCFSA advertising rules). Empty
   * until Ray supplies consented quotes; nothing renders while it is empty.
   */
  consentedTestimonials: [] as readonly { quote: string; attribution: string; consentedOn: string }[],
  /** Relta Real Estate is Ray's sole proprietorship, not a separate entity. */
  proprietorLine: 'Ray Rasouli, sole proprietor of Relta Real Estate',
  primaryContactEmailLabel: 'ray@relta.ca',
  primaryContactEmail: 'ray@relta.ca',
  primaryContactPhoneLabel: '+1 (778) 910-6917',
  phone: '+1-778-910-6917',
  brandPrimaryColorToken: 'brand.primary',
  websiteBaseUrl: 'https://www.relta.ca',
  social: {
    facebook: 'https://www.facebook.com/people/Relta-Real-Estate/61575450790202/',
    instagram: 'https://www.instagram.com/reltarealestate/',
    tiktok: 'https://www.tiktok.com/@reltarealestate',
    youtube: 'https://www.youtube.com/@ReltaRealEstate',
    linkedin: 'https://www.linkedin.com/in/ray-rasouli/',
  },
  /**
   * Third-party profiles that independently corroborate the entity. These feed
   * schema.org `sameAs`, which is what search and AI systems use to decide that
   * "Relta" is a distinct entity rather than a misspelling of Delta or Relata.
   * Social links prove activity; these prove identity, so they carry more weight.
   * Only add URLs that resolve to a live page naming Relta or Ray Rasouli.
   */
  authorityProfiles: ['https://www.bcfsa.ca/re-licencee/RE611959'] as readonly string[],
} as const;
